import { Injectable } from "@angular/core";
import { BILLING_STATUS, BillingRunSummary, getTotalModelCost, getUnsortedModelList, InformationAsset, ModelDetails, Organisation, OrganisationBilling, TagGroup } from "../common/organisation";
import { Linqset, LinqsetEdge, LinqsetNode, NODE_SUPERTYPE, getDurationHours, getInsightsForNode, getSuperTypeName, getTimesPerYearFromFrequency } from "../common/linqset-model";
import { environment } from "../../environments/environment";
import { NodeService } from "./node.service";
import { getUserRoleName } from "../common/user";
import { toCsv } from "./csv";

export enum EXPORT_TYPE {
    CSV,
    JSON,
    PDF,
    PNG,
    JPG,
    SYSTEM_HOURS_CSV,
    ROLE_HOURS_CSV
}

const MIME_TYPES:Record<string, string> = {
    csv: 'text/csv',
    json: 'application/json',
    html: 'text/html',
    text: 'text/plain',
}

// the billing states, in the order their columns appear
const BILLING_STATES = [
    BILLING_STATUS.CREATED, BILLING_STATUS.EXEMPT, BILLING_STATUS.EXPIRED_ORGANISATION, BILLING_STATUS.INVOICED,
    BILLING_STATUS.INVOICING_ERROR, BILLING_STATUS.MISSING_USAGE, BILLING_STATUS.OVERDUE, BILLING_STATUS.PAID,
    BILLING_STATUS.PAYMENT_ERROR, BILLING_STATUS.PREDICTION,
]
const BILLING_STATE_NAMES = ["created", "exempt", "expired", "invoiced", "error", "no usages", "overdue", "paid", "payment error", "prediction"]

@Injectable({
    providedIn: 'root'
})
export class DownloadService {

    constructor(private nodeService:NodeService) {}

    public createBillingRunCSV(runs:BillingRunSummary[]) {
        const rows = (runs || []).map(r => [
            r.runId,
            r.nodes?.number,
            r.nodes?.amount,
            r.organisations?.number,
            r.organisations?.amount,
            r.projects?.number,
            r.projects?.amount,
            r.users?.number,
            r.users?.amount,
            ...BILLING_STATES.map(s => r.states?.[s]?.number || 0),
            ...BILLING_STATES.map(s => r.states?.[s]?.amount || 0),
        ])

        const header = [
            "Run ID",
            "Number nodes",
            "Nodes",
            "Number organisations",
            "Organisations",
            "Number projects",
            "Projects",
            "Number users",
            "Users",
            ...BILLING_STATE_NAMES.map(n => "Num " + n),
            ...BILLING_STATE_NAMES.map(n => n[0].toUpperCase() + n.slice(1)),
        ]

        this.downloadFile(toCsv(header, rows), "LINQ billing", "csv")
    }

    public createOrgBillingCSV(records:OrganisationBilling[]) {
        if (!records?.length) return

        const rows = records.map(r => [
            r.runId,
            r.createdDate,
            r.organisationKey,
            r.organisationName,
            r.nodes?.number,
            r.nodes?.amount,
            r.projects?.number,
            r.projects?.amount,
            r.users?.number,
            r.users?.amount,
            r.amount,
            r.invoiceNumber||r.invoiceId,
            r.currencyCode,
            r.status,
            r.error
        ])

        const header = [
            "Run ID",
            "Created",
            "Key",
            "Name",
            "Number nodes",
            "Nodes",
            "Number projects",
            "Projects",
            "Number users",
            "Users",
            "Total",
            "Invoice ID",
            "Currency",
            "Status",
            "Error"
        ]

        this.downloadFile(toCsv(header, rows), "LINQ billing - " + records[0].runId, "csv")
    }

    public createCSVAdminDownload(admins:any[]) {
        const header = [
            "Organisation key",
            "Organisation name",
            "Partner",
            "Organisation CRM ID",
            "Organisation CRM Deal ID",
            "Created",
            "End date",
            "Last access",
            "Billing exempt",
            "State",
            "User name",
            "Email"
        ]
        const rows = (admins || []).map(admin => [
            admin.organisationKey,
            admin.organisationName,
            admin.isPartner,
            admin.organisationCRMId,
            admin.organisationCRMDealId,
            admin.createdDate,
            admin.endDate,
            admin.lastAccess,
            admin.isExemptFromPayment,
            admin.state,
            admin.userName,
            admin.email
        ])
        this.downloadFile(toCsv(header, rows), "LINQ administrators", "csv")
    }

    public createCSVUserDownload(users:any[]) {
        const header = [
            "Key",
            "Name",
            "Role",
            "Organisations",
            "Email",
            "Registered date",
            "Is activated",
            "Last login"
        ]
        const rows = (users || []).map(user => [
            user._key,
            user.name,
            getUserRoleName(user.role),
            user.organisations?user.organisations.join('|'):'',
            user.email,
            user.createdDate,
            user.activated,
            user.signInDate
        ])
        this.downloadFile(toCsv(header, rows), "LINQ users", "csv")
    }

    // saves text as a file named after what it holds and today's date. A CSV starts with a byte order mark so
    // spreadsheets read accented letters correctly
    public downloadFile(download:string, name:string, type:string) {
        const extension = type==="text"?"txt":type
        const content = type === "csv" ? "﻿" + download : download
        const blob = new Blob([content], {type: `${MIME_TYPES[type] || 'text/plain'};charset=utf-8`})
        const url = URL.createObjectURL(blob)

        const element = document.createElement('a');
        element.href = url
        element.download = `${name || "LINQ"} ${new Date().toDateString()}.${extension}`
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click(); // simulate click
        document.body.removeChild(element);
        // the download has started with the file; the browser can let it go once this has finished
        setTimeout(() => URL.revokeObjectURL(url))
    }

    public createJsonDownload(organisation:Organisation, linqset:Linqset) {
        const tagMap = (organisation.tagGroups || []).reduce((m, tg) => {
            for (const t of tg.tags || []) {
                m[t._key] = t.name
            }
            return m
        }, {})

        const tags:string[] = []
        const nodes = linqset.nodes.map(n => this.createDownloadNode(n, tagMap, tags))

        const download = {
            version: environment.version,
            name: linqset.model?.name,
            description: linqset.model?.description,
            function: linqset.model?.function,
            tags,
            nodes,
            edges:linqset.edges.map(e => this.createDownloadEdge(e)),
        }
        this.downloadFile(JSON.stringify(download), download.name, "json")
    }

    private createDownloadNode(n:LinqsetNode, tagMap:any, tags:string[]) {
        // tags from a tag group since deleted have no name, and are left out
        const nodeTags = Object.values(n.properties?.tagKeys || {}).map((k:string) => tagMap[k]).filter(name => name)
        nodeTags.forEach(nt => { if (tags.indexOf(nt) < 0) tags.push(nt) })

        const typeInfo = this.nodeService.getType(n)
        return {
            _key : n._key,
            superType: n.superType,
            superTypeDesc: typeInfo?.superType.name,
            type: n.type,
            typeDesc: typeInfo?.name,
            name: n.name,
            description: n.description,
            url: n.url,
            properties: n.properties,
            tags:nodeTags,
        }
    }

    private createDownloadEdge(e:LinqsetEdge) {
        return {
            _key : e._key,
            type: e.type,
            outputPercentage: e.outputPercentage,
            sourceKey: e.sourceKey,
            targetKey: e.targetKey,
            sourceVisualisationKey: e.sourceVisualisationKey,
            targetVisualisationKey: e.targetVisualisationKey
        }
    }

    // each role's or system's hours in each model: a row for each model and a column for each role or system
    public hoursByModelCSVDownload(organisationLinqset:Linqset, superType:NODE_SUPERTYPE) {
        const nodes = organisationLinqset.nodes.filter(n => n.superType === superType).sort((a,b) => (a.name||'').toLowerCase()>=(b.name||'').toLowerCase()?1:-1)

        const models = getUnsortedModelList(organisationLinqset.organisation, organisationLinqset.project)

        const header = ["Model", ...nodes.map(n => n.name || '')]
        const rows = models.map(m => [
            m.name || '',
            ...nodes.map(n => n.insights?.find(i => i.modelKey === m._key && i.projectKey === organisationLinqset.workspaceKey)?.hours || 0)
        ])

        this.downloadFile(toCsv(header, rows), organisationLinqset.organisation.name + "-" + getSuperTypeName(superType), "csv")
    }

    public createCSVDownload(organisation:Organisation, linqset:Linqset) {
        this.createCSVDownloadForNodes(organisation, linqset.nodes, linqset.modelKey, linqset.workspaceKey, linqset.model?.name)
    }

    public createCSVDownloadForModels(organisation:Organisation, models:ModelDetails[], workspaceKey:string, name:string) {
        const header = [
            "Key",
            "Name",
            "Description",
            "Created",
            "Updated",
            "Function",
            "Actions",
            "Hours",
            "Cost",
            "Outputs",
            "Carbon",
            "Capacity",
            "Tags"
        ]
        const rows = (models || []).map(m => this.createCSVModel(m, workspaceKey, organisation.modelTags))

        this.downloadFile(toCsv(header, rows), name, "csv")
    }

    public createCSVDownloadForNodes(organisation:Organisation, nodes:LinqsetNode[], modelKey:string, workspaceKey:string, name:string) {
        const tagGroups = organisation.tagGroups || []
        const rows = (nodes || []).map(n => this.createCSVNode(n, modelKey, workspaceKey, tagGroups, organisation.informationAssets || []))

        this.downloadFile(toCsv(this.createCSVHeader(tagGroups), rows), name, "csv")
    }

    private createCSVModel(model:ModelDetails, workspaceKey:string, tagGroup:TagGroup):unknown[] {
        const insights = model.insights?.find(i => i.projectKey === workspaceKey)
        const actions = insights?.superTypes?.[NODE_SUPERTYPE.ACTION]

        return [
            model._key,
            model.name,
            model.description,
            model.createdDate,
            model.updatedDate,
            model.function,
            actions?.number,
            actions?.hours,
            insights && getTotalModelCost(insights),
            insights?.numOutputsProcessed,
            insights?.allocatedCarbonByScope?.total,
            insights?.modelledCapacity,
            (model.tagKeys || []).map(k => tagGroup?.tags?.find(t => t._key === k)?.name).filter(t => t).join(" | ")
        ]
    }

    private createCSVHeader(tagGroups:TagGroup[]):string[] {
        return [
            "Key",
            "Name",
            "Description",
            "Type",
            "Sub type",
            "URL",
            "Notes",
            "Asset",
            "Duration hours",
            "Frequency per year",
            "Fixed annual cost",
            "Annual cost per person",
            "Annual cost per unit",
            "Hourly cost",
            "Num people",
            "Num units",
            "Lifetime",
            "Output value",
            "Output revenue",
            "% modelled",
            "Number processed",
            "Hours",
            "Cost",
            "Carbon",
            "Value",
            "Actions supported",
            "Outputs supported",
            "Info supplied",
            ...tagGroups.map(tg => tg.name)
        ]
    }

    private createCSVNode(node:LinqsetNode, modelKey:string, workspaceKey:string, tagGroups:TagGroup[], assets:InformationAsset[]):unknown[] {
        const typeInfo = this.nodeService.getType(node)
        const insights = getInsightsForNode(node, modelKey, workspaceKey)

        const asset = node.superType===NODE_SUPERTYPE.INFORMATION && node.informationAssetKey && assets.find(a => a._key === node.informationAssetKey)
        const isAction = node.superType===NODE_SUPERTYPE.ACTION

        return [
            node._key,
            node.name,
            node.description,
            typeInfo?.superType.name,
            typeInfo?.name,
            node.url,
            node.notes,
            asset?asset.name:'',
            isAction?Math.round(getDurationHours(node.properties?.duration)*100)/100:'',
            isAction?getTimesPerYearFromFrequency(node.properties?.frequency):'',
            node.properties?.fixedCost||0,
            node.properties?.annualCost||0,
            node.properties?.unitCost||0,
            node.properties?.hourlyCost||0,
            node.properties?.people||0,
            node.properties?.units||0,
            node.properties?.lifetime||0,
            node.properties?.outputValue||0,
            node.properties?.outputRevenue||0,
            node.properties?.percentageModelled||0,
            insights?.numberProcessed||0,
            insights?.hours||0,
            insights?.allocatedCostsByType?.total||0,
            insights?.allocatedCarbonByScope?.total||0,
            insights?.value||0,
            insights?.numActionsSupported||0,
            insights?.outputsSupported||0,
            insights?.numInfoSupplied||0,
            // every row has a column for each tag group; only actions are tagged
            ...tagGroups.map(tg => {
                const tagKey = isAction && node.properties?.tagKeys?.[tg._key]
                return (tagKey && tg.tags?.find(t => t._key === tagKey)?.name) || ""
            })
        ]
    }
}
